import React from "react";
import Image from "next/image";
import PropTypes from "prop-types";

const DEFAULT_SIZES = [
  "640w",
  "750w",
  "828w",
  "1080w",
  "1200w",
  "1920w",
  "2048w",
  "3840w",
];

const VALID_EXTENSIONS = [
  "avif",
  "webp",
  // "jxl",
];

export const makeUpSrcForApi = (src) => {
  let pathName = src;
  try {
    // eliminamos hosts
    pathName = pathName.replace("https://", "");
    pathName = pathName.replace("http://", "");
    pathName = pathName.replace("prod.cms.col.movistar.es", "");
    pathName = pathName.replace("qa.cms.col.movistar.es", "");
    pathName = pathName.replace("www.movistar.es", "");
    pathName = pathName.replace("col10mgmt.tsm.inet", "");
    pathName = pathName.replace("movistar.es", "");
    pathName = pathName.replace("//", "/");

    let srcSplit = pathName.split("?");
    srcSplit = srcSplit[0];
    srcSplit = srcSplit.split(".");
    pathName = srcSplit.join(".");
  } catch (e) {
    console.error(e);
  }
  return pathName;
};

const getApiHost = () => {
  let API = "{host}/api/v3/get-pic";
  const currentEnv = `${process.env.NEXT_PUBLIC_ENV_NAME}`;
  // Cambio host de backend segun entorno
  if (currentEnv === "prod") {
    API = API.replace("{host}", "https://ssr.col.movistar.es");
  } else if (
    currentEnv === "local" ||
    currentEnv === "dev" ||
    currentEnv === "qa" ||
    process.env.NEXT_PUBLIC_COL10IMAGES === "false"
  ) {
    // API = API.replace("{host}", "http://127.0.0.1:8080");
    API = API.replace("{host}", "https://dev.ssr.col.movistar.es");
  } else {
    API = "https://col10mgmt.tsm.inet";
  }
  return API;
};

const createExtensionImage = ({
  src,
  extension,
  oe = "",
  width = "",
  apiHost = true,
}) => {
  let pathname = makeUpSrcForApi(src);
  const API = getApiHost();
  pathname = pathname.split(".");
  pathname.pop();
  let queryParamsString = ";";
  const querysParams = [];
  if (width) {
    querysParams.push(`w=${width}`);
  }
  if (oe) {
    querysParams.push(`oe=${oe}`);
  }
  if (querysParams.length > 0) {
    queryParamsString = `?${querysParams.join("&")}`;
  }

  return `${apiHost ? API : ""}${makeUpSrcForApi(
    pathname[0]
  )}.${extension}${queryParamsString}`;
};

const getExtensionOfSrc = (src) => {
  const srcSplit = src.split(".");
  const extension = [srcSplit.pop()];
  return extension;
};

const getImageId = (entryPoint = "") => {
  let tmp = entryPoint.split(".");
  tmp = tmp[tmp.length - 1];
  tmp = tmp.split("-");
  return tmp[tmp.length - 1];
};

const createSrcSet = ({ src, oe, compressionByResize }) => {
  let sizesToGenerate = [...DEFAULT_SIZES];
  if (!compressionByResize) {
    sizesToGenerate = [sizesToGenerate.pop()];
  }
  let srcSet = [];
  [...VALID_EXTENSIONS, oe].forEach((extension) => {
    let srcSetOfExtension = "";
    sizesToGenerate.forEach((size) => {
      const w = size.replace("w", "");
      const newSrc = createExtensionImage({
        src,
        extension,
        apiHost: true,
        oe,
        width: w,
      });
      srcSetOfExtension = `${srcSetOfExtension}${newSrc} ${size},`;
    });
    srcSet.push(srcSetOfExtension);
  });
  return srcSet;
};

const Imagen = ({
  src,
  alt,
  title,
  fill,
  width,
  height,
  objectFit,
  objectPosition,
  className,
  custoObjectInfo,
  cyData,
  priority,
  onLoadCapture,
  onLoadingComplete,
  compressionByResize,
  display,
}) => {
  if (!src || !display) {
    return null;
  }
  const oe = getExtensionOfSrc(src);
  const srcSetArray = createSrcSet({ src, oe, compressionByResize });
  const basePictureSrc = srcSetArray[srcSetArray.length - 1];
  const objectStyle = {
    objectFit: objectFit ? objectFit : undefined,
    objectPosition: objectPosition ? objectPosition : undefined,
  };

  // Classname
  const finalClassName = `image${objectFit ? ` object-fit--${objectFit}` : ""}${
    objectPosition ? ` object-fit--${objectPosition}` : ""
  } ${className}`;

  return (
    <>
      {custoObjectInfo && (
        <span
          className={`image image-${getImageId()} custo-editable`}
          onClick={() => custoObjectInfo.handler(custoObjectInfo.entryPoint)}
        >
          {custoObjectInfo.entryPoint}
        </span>
      )}
      <picture>
        {oe !== "svg" &&
          srcSetArray.map((srcSet, index) => {
            const srcSetExtension = VALID_EXTENSIONS[index];
            return <source srcSet={srcSet} type={`image/${srcSetExtension}`} />;
          })}
        <Image
          style={objectStyle}
          alt={alt}
          title={title}
          srcSet={basePictureSrc}
          src={createExtensionImage({ src, extension: oe, oe })}
          width={width}
          unoptimized
          height={height}
          fill={fill}
          className={finalClassName}
          data-cy={cyData}
          priority={priority}
          onLoadCapture={onLoadCapture}
          onLoadingComplete={onLoadingComplete}
        />
      </picture>
      <style jsx>{`.c-image {
  &--show {
    opacity: 0 !important;
  }

  &--hide {
    opacity: 0 !important;
  }
}
`}</style>
    </>
  );
};

Imagen.propTypes = {
  /**
   * Url de la imagen.
   */
  src: PropTypes.string.isRequired,
  /**
   * Texto alternativo de la imagen.
   */
  alt: PropTypes.string.isRequired,
  /**
   * Ancho en píxeles de la imagen. Obligatorio excepto cuando se especifica la propiedad fill.
   */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Alto en píxeles de la imagen. Obligatorio excepto cuando se especifica la propiedad fill.
   */
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Estirará la imagen para ajustarse al contenedor padre en lugar de usar 'width' y 'height'. Cuando es 'true' es OBLIGATORIO asignarle al contenedor padre
   * uno de los siguientes estilos: position: "relative", position: "fixed" o position: "absolute".
   */
  fill: PropTypes.bool,
  /**
   * Establece cómo se debe redimensionar el contenido, para que se ajuste a su contenedor.
   */
  objectFit: PropTypes.oneOf([
    "fill",
    "contain",
    "cover",
    "none",
    "scale-down",
  ]),
  /**
   * Especifica la alineación del contenido del elemento dentro del elemento padre.
   */
  objectPosition: PropTypes.string,
  /**
   * Clase para añadirle estilos extras al icono.
   */
  className: PropTypes.string,
  /**
   * Para quitar loading-lazy.
   */
  priority: PropTypes.bool,
  /**
   * Objeto para la customización del icono en dev.
   */
  custoObjectInfo: PropTypes.shape({
    componentId: PropTypes.string,
    componentInstance: PropTypes.string,
    editFunction: PropTypes.func,
  }),
  cyData: PropTypes.string,
  /**
   * Booleano para controlar que se pinte o no el componente
   */
  display: PropTypes.bool,
};

Imagen.defaultProps = {
  fill: false,
  custoObjectInfo: null,
  priority: false,
  className: "",
  cyData: "",
  onLoadCapture: null,
  onLoadingComplete: null,
  display: true,
  compressionByResize: true,
};

export default Imagen;
